import { useState } from "react";
import { getThemeClasses } from "../../utils/ThemeClasses";
import type { ReportingCompany } from "../../features/dashboards/reporting";
import {
  classifyRisk,
  RISK_LABELS,
} from "../../features/dashboards/reportingConfig";
import { toCsv, downloadCsv } from "../../features/sg/csv";
import { fileDate } from "../../features/sg/exportRows";

const RISK_DOT: Record<ReturnType<typeof classifyRisk>, string> = {
  healthy: "bg-[#22c55e]",
  warning: "bg-[#eab308]",
  highRisk: "bg-[#f59e0b]",
  critical: "bg-[#ef4444]",
};

export default function AllClientsTable({
  platformName,
  companies,
  themeClasses,
}: {
  platformName: string;
  companies: ReportingCompany[];
  themeClasses: ReturnType<typeof getThemeClasses>;
}) {
  const [search, setSearch] = useState("");
  const q = search.trim().toLowerCase();
  const filtered = q
    ? companies.filter(
        (c) =>
          (c.user_name || "").toLowerCase().includes(q) ||
          (c.business_name || "").toLowerCase().includes(q),
      )
    : companies;
  const sorted = [...filtered].sort(
    (a, b) => a.effectiveness_pct - b.effectiveness_pct,
  );

  const exportCsv = () => {
    const headers = [
      "Usuario",
      "Razón social",
      "Total",
      "Reportando",
      "No reportando",
      "Efectividad %",
      "Categoría",
    ];
    const rows = sorted.map((c) => [
      c.user_name ?? "",
      c.business_name ?? "",
      c.total,
      c.reporting,
      c.not_reporting,
      c.effectiveness_pct,
      RISK_LABELS[classifyRisk(c.effectiveness_pct)],
    ]);
    downloadCsv(
      `entrega-servicio_todos_${platformName.toLowerCase()}_${fileDate()}.csv`,
      toCsv(headers, rows),
    );
  };

  return (
    <div
      className={`${themeClasses.cardBg} border ${themeClasses.cardBorder} rounded-xl overflow-hidden`}
    >
      <div
        className={`flex flex-col sm:flex-row sm:items-center justify-between gap-3 px-4 sm:px-6 py-3 sm:py-4 border-b ${themeClasses.cardBorder}`}
      >
        <div>
          <h3 className={`text-sm sm:text-base font-semibold ${themeClasses.text}`}>
            {platformName}
          </h3>
          <p className={`text-xs ${themeClasses.textSecondary}`}>
            {companies.length} clientes
          </p>
        </div>
        <div className="flex items-center gap-2 flex-shrink-0">
          <input
            type="text"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Buscar cliente…"
            className={`w-full sm:w-56 px-3 py-2 text-sm rounded-lg border ${themeClasses.cardBorder} ${themeClasses.cardBg} ${themeClasses.text} focus:outline-none`}
          />
          <button
            onClick={exportCsv}
            disabled={sorted.length === 0}
            className={`flex items-center gap-2 px-3 py-2 text-sm font-medium border ${themeClasses.cardBorder} rounded-lg ${themeClasses.text} hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors disabled:opacity-50`}
          >
            <i className="ri-download-line"></i>
            <span className="hidden sm:inline">CSV</span>
          </button>
        </div>
      </div>

      <div className="overflow-x-auto max-h-[460px] overflow-y-auto">
        <table className="w-full text-sm">
          <thead className={`sticky top-0 z-10 ${themeClasses.cardBg}`}>
            <tr className={`border-b ${themeClasses.cardBorder}`}>
              <th className={`text-left py-2 px-4 font-normal ${themeClasses.textSecondary} ${themeClasses.cardBg}`}>
                Cliente
              </th>
              <th className={`text-center py-2 px-3 font-normal ${themeClasses.textSecondary} ${themeClasses.cardBg}`}>
                Total
              </th>
              <th className={`text-center py-2 px-3 font-normal ${themeClasses.textSecondary} ${themeClasses.cardBg}`}>
                Reportando
              </th>
              <th className={`text-center py-2 px-3 font-normal ${themeClasses.textSecondary} ${themeClasses.cardBg}`}>
                No reportando
              </th>
              <th className={`text-center py-2 px-3 font-normal ${themeClasses.textSecondary} ${themeClasses.cardBg}`}>
                Efectividad
              </th>
              <th className={`text-left py-2 px-4 font-normal ${themeClasses.textSecondary} ${themeClasses.cardBg}`}>
                Categoría
              </th>
            </tr>
          </thead>
          <tbody>
            {sorted.length === 0 ? (
              <tr>
                <td
                  colSpan={6}
                  className={`py-8 text-center text-sm ${themeClasses.textSecondary}`}
                >
                  Sin resultados.
                </td>
              </tr>
            ) : (
              sorted.map((c) => {
                const bucket = classifyRisk(c.effectiveness_pct);
                return (
                  <tr key={c.user_id} className={`border-b ${themeClasses.cardBorder}`}>
                    <td className="py-2 px-4">
                      <p className={`font-medium ${themeClasses.text} truncate max-w-[220px]`}>
                        {c.business_name || c.user_name || `#${c.user_id}`}
                      </p>
                      {c.business_name && c.user_name && (
                        <p className={`text-xs ${themeClasses.textSecondary} truncate max-w-[220px]`}>
                          {c.user_name}
                        </p>
                      )}
                    </td>
                    <td className={`py-2 px-3 text-center ${themeClasses.text}`}>{c.total}</td>
                    <td className="py-2 px-3 text-center text-green-500 font-medium">{c.reporting}</td>
                    <td className="py-2 px-3 text-center text-red-500 font-medium">{c.not_reporting}</td>
                    <td className={`py-2 px-3 text-center font-semibold ${themeClasses.text}`}>
                      {c.effectiveness_pct}%
                    </td>
                    <td className="py-2 px-4">
                      <span className="inline-flex items-center gap-2">
                        <span className={`w-2 h-2 rounded-full ${RISK_DOT[bucket]}`}></span>
                        <span className={`text-xs ${themeClasses.textSecondary}`}>
                          {RISK_LABELS[bucket]}
                        </span>
                      </span>
                    </td>
                  </tr>
                );
              })
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}
